/**
 * Documents API - Presigned URLs for document upload and download
 */

import { axiosInstance } from '../axios.config';

export interface PresignedUploadResponse {
  uploadUrl: string;
  bucket: string;
  key: string;
  cdnUrl: string | null;
  expiresIn: number;
}

export const documentsApi = {
  /**
   * Get a short-lived presigned URL for viewing/downloading a document
   */
  getPresignedUrl: async (bucket: string, key: string): Promise<string> => {
    const response = await axiosInstance.get<{ success: boolean; data: { url: string } }>(
      '/api/v1/documents/presigned-url',
      { params: { bucket, key } }
    );
    if (!response.data.success || !response.data.data?.url) {
      throw new Error('Failed to get presigned URL');
    }
    return response.data.data.url;
  },

  /**
   * Get a presigned PUT URL for uploading a file directly to S3
   */
  getPresignedUploadUrl: async (
    folderPath: string,
    fileName: string,
    contentType: string,
    bucketType: 'public' | 'private' = 'private'
  ): Promise<PresignedUploadResponse> => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[documentsApi.getPresignedUploadUrl] request', {
        baseURL: axiosInstance.defaults.baseURL,
        folderPath,
        fileName,
        contentType,
        bucketType,
      });
    }
    const response = await axiosInstance.post<{ success: boolean; data: PresignedUploadResponse }>(
      '/api/v1/documents/presigned-upload-url',
      { folderPath, fileName, contentType, bucketType }
    );
    if (!response.data.success || !response.data.data?.uploadUrl) {
      throw new Error('Failed to get presigned upload URL');
    }
    return response.data.data;
  },
};
